const STORAGE_PREFIX = 'reschedule-mutation:';
const INTENT_STORAGE_PREFIX = 'reschedule-mutation-intent:';

export interface RescheduleMutationIntent {
  fingerprint: string;
  mutationId: string;
  originalStartsAt: string;
  selectedStartsAt: string;
}

export const rescheduleMutationFingerprint = (parts: string[]) =>
  JSON.stringify(parts);

const storageKey = (fingerprint: string) =>
  `${STORAGE_PREFIX}${encodeURIComponent(fingerprint)}`;

export const getRescheduleMutationId = (fingerprint: string): string => {
  const key = storageKey(fingerprint);
  const stored = window.sessionStorage.getItem(key);
  if (stored) return stored;

  const mutationId = window.crypto.randomUUID();
  window.sessionStorage.setItem(key, mutationId);
  return mutationId;
};

export const clearRescheduleMutationId = (fingerprint: string) => {
  window.sessionStorage.removeItem(storageKey(fingerprint));
};

const intentStorageKey = (intentKey: string) =>
  `${INTENT_STORAGE_PREFIX}${intentKey}`;

const isRescheduleMutationIntent = (
  value: unknown,
): value is RescheduleMutationIntent => {
  if (!value || typeof value !== 'object') return false;

  const intent = value as Partial<RescheduleMutationIntent>;
  return (
    typeof intent.fingerprint === 'string' &&
    typeof intent.mutationId === 'string' &&
    typeof intent.originalStartsAt === 'string' &&
    typeof intent.selectedStartsAt === 'string'
  );
};

export const getStoredRescheduleMutationIntent = (
  intentKey: string,
): RescheduleMutationIntent | null => {
  try {
    const stored = window.sessionStorage.getItem(intentStorageKey(intentKey));
    if (!stored) return null;

    const parsed: unknown = JSON.parse(stored);
    return isRescheduleMutationIntent(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const getRescheduleMutationIntent = (
  intentKey: string,
  create: () => RescheduleMutationIntent,
): RescheduleMutationIntent => {
  const stored = getStoredRescheduleMutationIntent(intentKey);
  if (stored) return stored;

  const intent = create();
  try {
    window.sessionStorage.setItem(
      intentStorageKey(intentKey),
      JSON.stringify(intent),
    );
  } catch {
    // The current attempt can continue even when persistence is unavailable.
  }
  return intent;
};

export const clearRescheduleMutationIntent = (intentKey: string) => {
  try {
    window.sessionStorage.removeItem(intentStorageKey(intentKey));
  } catch {
    // Best-effort cleanup for restricted or unavailable browser storage.
  }
};

export const isNonRetryableClientError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object' || !('status' in error)) return false;
  const status = Number(error.status);
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
};
